import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconAttach, IconCheck, IconUpload } from '../icons';
import { PORTAL_FIXTURE } from '../mock/portalFixture';
import { usePortalNavigation, usePortalQuery } from '../hooks/usePortalQuery';
import { usePortalCopy, usePortalUi } from '../PortalUi';
import { ScheduleModal } from './ScheduleModal';
import { PortalButton, PortalInput, PortalModal, PortalModalSpacer, PortalTextArea, PortalToggle } from '../ui';
import {
  deleteRequirementFile,
  deliverRequirement,
  formatOfferAmount,
  formatOfferMoney,
  formatOfferPremium,
  isAllowedRequirementFile,
  REQ_FILE_ACCEPT,
  REQ_FILE_CAMERA_ACCEPT,
  isPortalHttpError,
  PortalOffer,
  PortalOfferItem,
  PortalRequirement,
  PortalRequirementFile,
  uploadRequirementFile,
  offerRequiresSignature,
} from '../api/portalService';
import { clearOfferSignatureMark, markOfferSignatureStarted, startOfferSignature } from '../api/offerSignatureService';
import { agentContactLinks } from '../api/agentContact';
import { AgentContactButtons } from '../board/AgentContactButtons';
import { REACT_APP_URL_LOGALTY_SIGNATURE } from '../../config';
import { createLogger } from '../../utils/logger';

const offerSignatureLogger = createLogger('AcceptOfferModal');

/**
 * Cada modal se monta solo mientras está activo, así su estado local arranca limpio en cada apertura.
 * El overlay, Escape y click-afuera viven en `PortalModal`.
 */
export function PortalModals() {
  const { modal } = usePortalUi();
  switch (modal) {
    case 'upload':
      return <UploadModal />;
    case 'sign':
      return <SignModal />;
    case 'quest':
      return <QuestionnaireModal />;
    case 'reply':
      return <ReplyModal />;
    case 'schedule':
      return <ScheduleModal />;
    case 'accept':
      return <AcceptOfferModal />;
    case 'decline':
      return <DeclineOfferModal />;
    case 'pay':
      return <PayModal />;
    case 'notify':
      return <NotifyModal />;
    default:
      return null;
  }
}

function DoneBlock({ title, lead, icon }: { title: string; lead: string; icon?: React.ReactNode }) {
  return (
    <div className="mdone">
      <div className="icon-wrap">{icon ?? <IconCheck size={24} />}</div>
      <h2>{title}</h2>
      <p>{lead}</p>
    </div>
  );
}

function canCapturePhoto(): boolean {
  return typeof navigator !== 'undefined' && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
}

function partsCovered(
  parts: PortalRequirement['parts'],
  files: PortalRequirementFile[],
): boolean {
  const required = parts.filter((part) => part.required);
  if (required.length === 0) return true;
  const covered = new Set([
    ...files.map((file) => file.partCode).filter(Boolean),
    ...parts.filter((part) => part.covered).map((part) => part.code),
  ]);
  return required.every((part) => covered.has(part.code));
}

function defaultPartCode(parts: PortalRequirement['parts'], files: PortalRequirementFile[]): string {
  const covered = new Set(files.map((file) => file.partCode).filter(Boolean));
  const next = parts.find((part) => part.required && !covered.has(part.code) && !part.covered);
  return next?.code || parts[0]?.code || '';
}

function UploadModal() {
  const { liveRequirement } = usePortalUi();
  if (liveRequirement) return <LiveUploadModal requirement={liveRequirement} />;
  return <MockUploadModal />;
}

function MockUploadModal() {
  const t = usePortalCopy();
  const { step, setStep, uploadTitle, closeModal, markDone } = usePortalUi();
  const [file, setFile] = useState({ name: 'archivo', size: '—' });
  const [bar, setBar] = useState(0);

  const simulate = (name: string, size: string) => {
    setFile({ name, size });
    setBar(0);
    setStep(2);
    window.setTimeout(() => setBar(62), 150);
    window.setTimeout(() => setBar(100), 780);
    window.setTimeout(() => setStep(3), 1350);
  };

  return (
    <PortalModal
      id="m-upload"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={step === 3 ? (
        <>
          <PortalModalSpacer />
          <PortalButton label={t('done')} onClick={markDone} />
        </>
      ) : undefined}
    >
      {step === 1 && (
        <>
          <p className="eyebrow">{t('uploadEyebrow')}</p>
          <h2 id="up-title">{uploadTitle}</h2>
          <p>{t('uploadLead')}</p>
          <div className="drop">
            <IconUpload />
            <b>{t('uploadDrop')}</b>
            <small>{t('uploadHint')}</small>
            <div className="acts">
              <PortalButton variant="secondary" size="sm" label={t('takePhoto')} onClick={() => simulate('IMG_4471.HEIC', '3.2 MB')} />
              <PortalButton size="sm" label={t('chooseFile')} onClick={() => simulate('licencia-frente.jpg', '1.8 MB')} />
            </div>
          </div>
        </>
      )}
      {step === 2 && (
        <>
          <p className="eyebrow">{t('uploadingEyebrow')}</p>
          <h2>{t('uploadingTitle')}</h2>
          <p>{t('uploadingLead')}</p>
          <div className="filerow">
            <div className="fi">IMG</div>
            <div className="fn">
              <b id="up-file">{file.name}</b>
              <span id="up-size">{file.size}</span>
              <div className="bar"><i id="up-bar" style={{ width: `${bar}%` }} /></div>
            </div>
          </div>
        </>
      )}
      {step === 3 && <DoneBlock title={t('uploadDoneTitle')} lead={t('uploadDoneLead')} />}
    </PortalModal>
  );
}

function SignModal() {
  const t = usePortalCopy();
  const { step, setStep, closeModal, markDone } = usePortalUi();
  const [ok, setOk] = useState(false);
  const [name, setName] = useState('');
  const canSign = ok && name.trim().length > 0;

  return (
    <PortalModal
      id="m-sign"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={step === 1 ? (
        <>
          <PortalModalSpacer />
          <PortalButton variant="ghost" label={t('cancel')} onClick={closeModal} />
          <PortalButton label={t('signCta')} disabled={!canSign} onClick={() => setStep(2)} />
        </>
      ) : (
        <>
          <PortalModalSpacer />
          <PortalButton label={t('done')} onClick={markDone} />
        </>
      )}
    >
      {step === 1 && (
        <>
          <p className="eyebrow">{t('signEyebrow')}</p>
          <h2>{t('signTitle')}</h2>
          <p>{t('signLead')}</p>
          <div className="doc">{t('signDoc').split('\n\n').map((para) => <React.Fragment key={para}>{para}<br /><br /></React.Fragment>)}</div>
          <label className="check">
            <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />
            {t('signCheck')}
          </label>
          <PortalInput
            id="sign-name"
            label={t('signNameLabel')}
            placeholder={PORTAL_FIXTURE.fullName}
            value={name}
            wrapperStyle={{ marginTop: 16 }}
            onChange={(e) => setName(e.target.value)}
          />
        </>
      )}
      {step === 2 && <DoneBlock title={t('signDoneTitle')} lead={t('signDoneLead')} />}
    </PortalModal>
  );
}

function QuestionnaireModal() {
  const t = usePortalCopy();
  const { step, setStep, closeModal, markDone, showToast } = usePortalUi();
  return (
    <PortalModal
      id="m-quest"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={step === 1 ? (
        <>
          <PortalButton
            variant="ghost"
            label={t('questSave')}
            onClick={() => {
              closeModal();
              showToast(t('toastSaved'));
            }}
          />
          <PortalModalSpacer />
          <PortalButton label={t('next')} onClick={() => setStep(2)} />
        </>
      ) : (
        <>
          <PortalModalSpacer />
          <PortalButton label={t('done')} onClick={markDone} />
        </>
      )}
    >
      {step === 1 && (
        <>
          <p className="eyebrow">{t('questEyebrow')}</p>
          <h2>{t('questStepTitle')}</h2>
          <div className="bar" style={{ margin: '0 0 20px' }}><i style={{ width: '8%' }} /></div>
          <p style={{ color: 'var(--ink900)', fontWeight: 700, fontSize: 14.5 }}>{t('questQ1')}</p>
          <label className="qopt"><input type="radio" name="q1" />{t('questNo')}</label>
          <label className="qopt"><input type="radio" name="q1" />{t('questOcc')}</label>
          <label className="qopt"><input type="radio" name="q1" />{t('questDaily')}</label>
        </>
      )}
      {step === 2 && <DoneBlock title={t('questDoneTitle')} lead={t('questDoneLead')} />}
    </PortalModal>
  );
}

function LiveUploadModal({ requirement }: { requirement: PortalRequirement }) {
  const t = usePortalCopy();
  const { quoteID } = usePortalQuery();
  const { closeModal, showToast, requirementRefetch } = usePortalUi();
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<PortalRequirementFile[]>(requirement.files);
  const [text, setText] = useState(requirement.clientResponse || '');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [partByName, setPartByName] = useState<Record<string, string>>({});
  const capture = canCapturePhoto();
  const hasParts = requirement.parts.length > 0;

  const covered = useMemo(() => partsCovered(requirement.parts, files), [requirement.parts, files]);
  const canSend = !busy
    && (!requirement.requiresFile || files.length > 0)
    && covered
    && (!requirement.requiresResponse || text.trim().length > 0);

  const refreshAfterChange = () => {
    requirementRefetch?.();
  };

  const handleCloseOnConflict = () => {
    closeModal();
    refreshAfterChange();
  };

  const addFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setError(null);
    const queued = Array.from(list);
    // The FileList is processed in one turn: `files` state does not advance
    // between iterations, so each file picks the next uncovered part itself.
    let currentFiles = files;
    let selectedPart = hasParts ? (partByName.__next || '') : '';
    setBusy(true);
    try {
      for (const file of queued) {
        const allowed = isAllowedRequirementFile(file);
        if (!allowed.ok) {
          setError(allowed.message);
          continue;
        }
        const partCode = hasParts
          ? (selectedPart || defaultPartCode(requirement.parts, currentFiles))
          : undefined;
        selectedPart = '';
        setProgress((prev) => ({ ...prev, [file.name]: 10 }));
        try {
          const uploaded = await uploadRequirementFile(quoteID, requirement.id, file, partCode);
          currentFiles = [...currentFiles, uploaded];
          setFiles(currentFiles);
          setProgress((prev) => ({ ...prev, [file.name]: 100, [uploaded.id]: 100 }));
          refreshAfterChange();
        } catch (err) {
          if (isPortalHttpError(err) && err.status === 409) {
            handleCloseOnConflict();
            return;
          }
          setError(err instanceof Error ? err.message : t('reqLoadError'));
          setProgress((prev) => ({ ...prev, [file.name]: 0 }));
        }
      }
      if (hasParts) {
        setPartByName((prev) => {
          const next = { ...prev };
          delete next.__next;
          return next;
        });
      }
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
      if (cameraInput.current) cameraInput.current.value = '';
    }
  };

  const removeFile = async (file: PortalRequirementFile) => {
    setError(null);
    setBusy(true);
    try {
      await deleteRequirementFile(quoteID, requirement.id, file.id);
      setFiles((prev) => prev.filter((item) => item.id !== file.id));
      refreshAfterChange();
    } catch (err) {
      if (isPortalHttpError(err) && err.status === 409) {
        handleCloseOnConflict();
        return;
      }
      setError(err instanceof Error ? err.message : t('reqLoadError'));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setError(null);
    setBusy(true);
    try {
      await deliverRequirement(quoteID, requirement.id, requirement.requiresResponse ? text.trim() : undefined);
      closeModal();
      showToast(t('toastReceived'));
      refreshAfterChange();
    } catch (err) {
      if (isPortalHttpError(err) && err.status === 409) {
        handleCloseOnConflict();
        return;
      }
      setError(err instanceof Error ? err.message : t('reqLoadError'));
    } finally {
      setBusy(false);
    }
  };

  const accept = REQ_FILE_ACCEPT;

  return (
    <PortalModal
      id="m-upload"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={(
        <>
          <PortalModalSpacer />
          <PortalButton variant="ghost" label={t('cancel')} onClick={closeModal} />
          <PortalButton label={t('send')} disabled={!canSend} onClick={send} />
        </>
      )}
    >
      <p className="eyebrow">{t('uploadEyebrow')}</p>
      <h2 id="up-title">{requirement.clientLabel}</h2>
      <p>{t('uploadLeadLive')}</p>
      {error ? <p role="alert" style={{ color: '#B23039', fontWeight: 600 }}>{error}</p> : null}
      <input
        ref={fileInput}
        type="file"
        accept={accept}
        multiple
        hidden
        data-testid="req-file-input"
        onChange={(event) => addFiles(event.target.files)}
      />
      {capture ? (
        <input
          ref={cameraInput}
          type="file"
          accept={REQ_FILE_CAMERA_ACCEPT}
          capture="environment"
          hidden
          data-testid="req-camera-input"
          onChange={(event) => addFiles(event.target.files)}
        />
      ) : null}
      <div className="drop">
        <IconUpload />
        <b>{t('uploadDrop')}</b>
        <small>{t('uploadHintLive')}</small>
        <div className="acts">
          {capture ? (
            <PortalButton variant="secondary" size="sm" label={t('takePhoto')} onClick={() => cameraInput.current?.click()} />
          ) : null}
          <PortalButton size="sm" label={t('chooseFile')} onClick={() => fileInput.current?.click()} />
        </div>
      </div>
      {hasParts ? (
        <label className="lbl" style={{ marginTop: 16 }}>
          {t('reqPartLabel')}
          <select
            data-testid="req-part-select"
            value={partByName.__next || defaultPartCode(requirement.parts, files)}
            onChange={(event) => setPartByName((prev) => ({ ...prev, __next: event.target.value }))}
            style={{ display: 'block', marginTop: 8, width: '100%' }}
          >
            {requirement.parts.map((part) => (
              <option key={part.code} value={part.code}>
                {part.name || part.code}{part.required ? ' *' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {files.map((file) => (
        <div className="filerow" key={file.id}>
          <div className="fi">FILE</div>
          <div className="fn">
            <b>{file.originalName}</b>
            <span>{file.partCode || file.contentType}</span>
            <div className="bar"><i style={{ width: `${progress[file.id] ?? progress[file.originalName] ?? 100}%` }} /></div>
          </div>
          <PortalButton variant="ghost" size="sm" label={t('reqRemoveFile')} onClick={() => removeFile(file)} />
        </div>
      ))}
      {requirement.requiresResponse ? (
        <PortalTextArea
          placeholder={t('replyPlaceholder')}
          value={text}
          onChange={(event) => setText(event.target.value)}
          style={{ marginTop: 16 }}
        />
      ) : null}
    </PortalModal>
  );
}

function LiveReplyModal({ requirement }: { requirement: PortalRequirement }) {
  const t = usePortalCopy();
  const { quoteID } = usePortalQuery();
  const { closeModal, showToast, requirementRefetch } = usePortalUi();
  const [text, setText] = useState(requirement.clientResponse || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setError(null);
    setBusy(true);
    try {
      await deliverRequirement(quoteID, requirement.id, text.trim());
      closeModal();
      showToast(t('toastReceived'));
      requirementRefetch?.();
    } catch (err) {
      if (isPortalHttpError(err) && err.status === 409) {
        closeModal();
        requirementRefetch?.();
        return;
      }
      setError(err instanceof Error ? err.message : t('reqLoadError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PortalModal
      id="m-reply"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={(
        <>
          <PortalModalSpacer />
          <PortalButton variant="ghost" label={t('cancel')} onClick={closeModal} />
          <PortalButton label={t('send')} disabled={busy || !text.trim()} onClick={send} />
        </>
      )}
    >
      <p className="eyebrow">{t('replyEyebrow')}</p>
      <h2>{requirement.clientLabel}</h2>
      {requirement.noteToClient.trim() ? (
        <div className="uw-note" style={{ margin: '0 0 18px' }}>
          <b>{t('uwNote')}</b>
          {requirement.noteToClient}
        </div>
      ) : null}
      {error ? <p role="alert" style={{ color: '#B23039', fontWeight: 600 }}>{error}</p> : null}
      <PortalTextArea placeholder={t('replyPlaceholder')} value={text} onChange={(event) => setText(event.target.value)} />
    </PortalModal>
  );
}

function ReplyModal() {
  const t = usePortalCopy();
  const { liveRequirement, step, setStep, closeModal, markDone, showToast } = usePortalUi();
  const [text, setText] = useState('');
  if (liveRequirement) return <LiveReplyModal requirement={liveRequirement} />;
  return (
    <PortalModal
      id="m-reply"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={step === 1 ? (
        <>
          <PortalModalSpacer />
          <PortalButton variant="ghost" label={t('cancel')} onClick={closeModal} />
          <PortalButton label={t('send')} onClick={() => setStep(2)} />
        </>
      ) : (
        <>
          <PortalModalSpacer />
          <PortalButton label={t('done')} onClick={markDone} />
        </>
      )}
    >
      {step === 1 && (
        <>
          <p className="eyebrow">{t('replyEyebrow')}</p>
          <h2>{t('medTitle')}</h2>
          <div className="uw-note" style={{ margin: '0 0 18px' }}>
            <b>{t('replyAsked')}</b>
            {t('medNote')}
          </div>
          <PortalTextArea placeholder={t('replyPlaceholder')} value={text} onChange={(e) => setText(e.target.value)} />
          <button className="attach" type="button" style={{ marginTop: 12 }} onClick={() => showToast(t('toastAttach'))}>
            <IconAttach />
            {t('replyAttach')}
          </button>
        </>
      )}
      {step === 2 && <DoneBlock title={t('replyDoneTitle')} lead={t('replyDoneLead')} />}
    </PortalModal>
  );
}

type RecapLine = { key: string; label: string; value: string; before: string };

function itemCodes(items: PortalOfferItem[] | undefined): Set<string> {
  return new Set((items || []).map((item) => item.code).filter(Boolean));
}

/**
 * Misma regla que «Se cambia» en el tablero: sin monto vigente no se afirma
 * que el add-on cambió. El portal no inventa esa fila.
 */
function coverageAmountChanged(current: PortalOfferItem, proposed: PortalOfferItem): boolean {
  const previous = current.amount ?? 0;
  const next = proposed.amount ?? 0;
  if (previous <= 0 || next <= 0) return false;
  return previous !== next;
}

/** Misma etiqueta que el tablero: el texto del ítem, si no el catálogo, si no el código. */
function coverageName(item: PortalOfferItem, labels: Record<string, string>): string {
  const text = item.text?.trim();
  if (text) return text;
  return labels[item.code] || item.code;
}

/**
 * Recap en vivo: una fila solo si la oferta trae ese cambio completo.
 * Prima y monto piden los dos lados. Coberturas cuenta las que se quitan
 * (código en current que no está en proposed) y, aparte, una fila por add-on
 * que sigue pero con otro monto. Exclusiones cuenta las nuevas.
 * Lo que el canal del cliente no manda no se rellena con la maqueta.
 */
function liveRecapLines(
  offer: PortalOffer,
  labels: Record<string, string>,
  t: (
    key: 'acceptPremium' | 'acceptCoverage' | 'acceptRiders' | 'acceptExclusions' | 'acceptRidersDropped' | 'acceptExclusionsAdded',
    vars?: Record<string, string | number>,
  ) => string,
): RecapLine[] {
  const lines: RecapLine[] = [];
  offer.changes.forEach((change) => {
    if (change.field === 'premium') {
      const value = formatOfferPremium(change.proposed);
      const before = formatOfferPremium(change.current);
      if (!value || !before) return;
      lines.push({ key: change.field, label: t('acceptPremium'), value, before });
      return;
    }
    if (change.field === 'sum_assured') {
      const value = formatOfferAmount(change.proposed);
      const before = formatOfferAmount(change.current);
      if (!value || !before) return;
      lines.push({ key: change.field, label: t('acceptCoverage'), value, before });
      return;
    }
    if (change.field === 'coverages') {
      const current = change.currentItems || [];
      const proposedItems = change.proposedItems || [];
      const proposed = itemCodes(proposedItems);
      const dropped = current.filter((item) => item.code && !proposed.has(item.code)).length;
      if (current.length > 0 && dropped > 0) {
        lines.push({
          key: change.field,
          label: t('acceptRiders'),
          value: t('acceptRidersDropped', { dropped, total: current.length }),
          before: '',
        });
      }
      proposedItems.forEach((item) => {
        const previous = current.find((row) => row.code === item.code);
        if (!previous || !coverageAmountChanged(previous, item)) return;
        lines.push({
          key: `coverage:${item.code}`,
          label: coverageName(item, labels),
          value: formatOfferMoney(item.amount ?? 0),
          before: formatOfferMoney(previous.amount ?? 0),
        });
      });
      return;
    }
    if (change.field === 'exclusions') {
      const current = itemCodes(change.currentItems);
      const added = (change.proposedItems || []).filter((item) => item.code && !current.has(item.code)).length;
      if (added === 0) return;
      lines.push({
        key: change.field,
        label: t('acceptExclusions'),
        value: t('acceptExclusionsAdded', { count: added }),
        before: '',
      });
    }
  });
  return lines;
}

function AcceptOfferModal() {
  const t = usePortalCopy();
  const { quoteID } = usePortalQuery();
  const { closeModal, offerOutcome, liveOffer, liveOfferLabels } = usePortalUi();
  // UWT-79: INT acepta sin firma — «Aceptar» registra la respuesta con el clic.
  const needsSignature = offerRequiresSignature(liveOffer);
  const formRef = useRef<HTMLFormElement>(null);
  const aliveRef = useRef(true);
  const markedRef = useRef(false);
  const submittedRef = useRef(false);
  const [ok, setOk] = useState(false);
  const [starting, setStarting] = useState(false);
  const [samlValue, setSamlValue] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // En vivo el tablero registra offerOutcome y «Continuar» llama a Logalty.
  // La maqueta (?state=) no tiene oferta: cierra el modal y no muestra nada más.
  const live = Boolean(offerOutcome);
  const lines = live
    ? (liveOffer ? liveRecapLines(liveOffer, liveOfferLabels, t) : [])
    : [
      { key: 'premium', label: t('acceptPremium'), value: PORTAL_FIXTURE.offerPremium, before: PORTAL_FIXTURE.premium },
      { key: 'coverage', label: t('acceptCoverage'), value: PORTAL_FIXTURE.offerCoverage, before: PORTAL_FIXTURE.coverage },
      { key: 'riders', label: t('acceptRiders'), value: t('chgRidersDelta'), before: '' },
      { key: 'exclusions', label: t('acceptExclusions'), value: t('chgExclusionsDelta'), before: '' },
    ];

  // Si el modal se va antes del POST a Logalty, la marca de espera no puede
  // quedar: el tablero se quedaría cinco minutos sin aceptar ni rechazar.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (markedRef.current && !submittedRef.current) clearOfferSignatureMark();
    };
  }, []);

  // El formulario se manda solo en cuanto hay asertación: el cliente ya
  // confirmó con el tilde y con «Continuar a la firma».
  useEffect(() => {
    if (!samlValue || !formRef.current) return undefined;
    const timer = window.setTimeout(() => {
      submittedRef.current = true;
      formRef.current?.submit();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [samlValue]);

  const confirm = async () => {
    if (!live) {
      closeModal();
      return;
    }
    if (!quoteID || !liveOffer || starting) return;
    setStarting(true);
    setFailed(false);
    if (!needsSignature) {
      try {
        await offerOutcome?.('accepted');
        if (aliveRef.current) closeModal();
      } catch (error) {
        // 409: la oferta ya se respondió (o venció). El tablero ya se refrescó.
        if (isPortalHttpError(error) && error.status === 409) {
          if (aliveRef.current) closeModal();
          return;
        }
        offerSignatureLogger.error('No se pudo registrar la aceptación de la oferta:', error);
        if (!aliveRef.current) return;
        setFailed(true);
        setStarting(false);
      }
      return;
    }
    try {
      const saml = await startOfferSignature({ quoteId: quoteID, offerId: liveOffer.id });
      if (!aliveRef.current) return;
      // Se marca ANTES de salir: cuando vuelva, el tablero espera en vez de
      // volver a ofrecerle firmar.
      markOfferSignatureStarted(quoteID);
      markedRef.current = true;
      setSamlValue(saml);
    } catch (error) {
      offerSignatureLogger.error('No se pudo iniciar la firma de la oferta:', error);
      if (!aliveRef.current) return;
      setFailed(true);
      setStarting(false);
    }
  };

  const leaving = starting || Boolean(samlValue);
  const continueLabel = needsSignature
    ? (leaving ? t('signOfferRedirecting') : t('acceptContinue'))
    : (leaving ? t('acceptConfirming') : t('acceptOfferConfirm'));
  const dismiss = () => {
    if (leaving) return;
    closeModal();
  };

  return (
    <PortalModal
      id="m-accept"
      testId="portal-accept-overlay"
      onClose={dismiss}
      dismissable={!leaving}
      closeLabel={t('close')}
      footer={(
        <>
          <PortalModalSpacer />
          <PortalButton variant="ghost" label={t('acceptBack')} onClick={dismiss} disabled={leaving} />
          <PortalButton
            label={continueLabel}
            disabled={!ok || leaving || (live && !liveOffer)}
            onClick={() => { void confirm(); }}
          />
        </>
      )}
    >
      <form
        data-testid="portal-firma-oferta-form"
        ref={formRef}
        method="POST"
        action={REACT_APP_URL_LOGALTY_SIGNATURE}
        style={{ display: 'none' }}
      >
        <input type="hidden" name="saml_assertion" value={samlValue || ''} />
      </form>
      <>
          <p className="eyebrow warn">{t('acceptEyebrow')}</p>
          <h2>{t('acceptTitle')}</h2>
          <p>{t('acceptLead')}</p>
          {lines.length > 0 ? (
            <ul className="recap" data-testid="offer-accept-recap">
              {lines.map((line) => (
                <li key={line.key}>
                  <span>{line.label}</span>
                  <b>
                    {line.value}
                    {line.before ? (
                      <>
                        {' '}
                        <span style={{ color: 'var(--ink400)', fontWeight: 600 }}>({t('acceptBefore')} {line.before})</span>
                      </>
                    ) : null}
                  </b>
                </li>
              ))}
            </ul>
          ) : null}
          <label className="check">
            <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />
            {t('acceptCheck')}
          </label>
          {live && needsSignature ? (
            <p className="foot-note" data-spec="o-sign-reject-warning">{t('signOfferRejectWarning')}</p>
          ) : null}
          {live && !liveOffer ? (
            <p className="foot-note" data-spec="o-sign-no-offer">{t('signOfferGone')}</p>
          ) : null}
          {failed ? (
            <p className="foot-note" data-spec="o-sign-offer-error">{needsSignature ? t('signOfferError') : t('acceptOfferError')}</p>
          ) : null}
      </>
    </PortalModal>
  );
}

function DeclineOfferModal() {
  const t = usePortalCopy();
  const navigate = useNavigate();
  const { path } = usePortalNavigation();
  const { closeModal, showToast, offerOutcome, liveStatus, mocked } = usePortalUi();
  const [busy, setBusy] = useState(false);

  // Decide by `mocked`, never by null-coalescing live agent → fixture (OUW-88).
  const agent = mocked
    ? {
        name: PORTAL_FIXTURE.agent.name,
        phone: PORTAL_FIXTURE.agent.waNumber,
        email: PORTAL_FIXTURE.agent.email,
      }
    : (liveStatus?.agent ?? { name: '', phone: '', email: '' });
  const links = agentContactLinks(agent, 'Solicitud');
  const talkName = mocked
    ? (links.talkName || PORTAL_FIXTURE.agent.firstName)
    : (links.talkName || t('yourAgent'));
  // Product: WhatsApp primary, email secondary.
  const showTalk = mocked || Boolean(links.wa || links.mail);

  const confirmDecline = async () => {
    if (!offerOutcome) {
      closeModal();
      navigate(path('/status/solicitud', { state: 'cancelled', dropView: true }));
      showToast(t('toastDeclined'));
      return;
    }
    setBusy(true);
    try {
      await offerOutcome('rejected');
      closeModal();
    } catch (error) {
      if (isPortalHttpError(error) && error.status === 409) {
        closeModal();
        return;
      }
      showToast(t('offerWriteError'));
    } finally {
      setBusy(false);
    }
  };

  const onTalk = () => {
    if (mocked) {
      showToast(t('toastAgentDemo'));
      closeModal();
      return;
    }
    closeModal();
  };

  return (
    <PortalModal
      id="m-decline"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={(
        <>
          <PortalButton
            variant="ghost"
            label={busy ? t('offerWriteBusy') : t('declineYes')}
            disabled={busy}
            onClick={() => { void confirmDecline(); }}
          />
          <PortalModalSpacer />
          {showTalk ? (
            mocked ? (
              <PortalButton
                label={t('declineTalk', { name: talkName })}
                disabled={busy}
                onClick={onTalk}
              />
            ) : (
              <AgentContactButtons
                talkName={talkName}
                wa={links.wa}
                mail={links.mail}
                writeLabel={t('declineTalk', { name: talkName })}
                emailLabel={t('emailAgent', { name: talkName })}
                waSpec="o-agent-wa"
                mailSpec="o-agent-mail"
                busy={busy}
                waVariant="primary"
              />
            )
          ) : null}
        </>
      )}
    >
      <p className="eyebrow danger">{t('declineEyebrow')}</p>
      <h2>{t('declineTitle')}</h2>
      <p>{t('declineLead', { name: talkName })}</p>
    </PortalModal>
  );
}

function PayModal() {
  const t = usePortalCopy();
  const navigate = useNavigate();
  const { path } = usePortalNavigation();
  const { step, setStep, payMode, payAmount, closeModal } = usePortalUi();
  // OUW-200 (H4): la prima real viene del panel que abrió el modal; sin dato en
  // vivo (mock ?state=…) queda la del prototipo.
  const amount = payAmount ?? PORTAL_FIXTURE.premium;
  const [method, setMethod] = useState<'card' | 'ach'>('card');
  const [consent, setConsent] = useState(false);
  const cobro = payMode === 'pay';

  return (
    <PortalModal
      id="m-pay"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={step === 1 ? (
        <>
          <PortalModalSpacer />
          <PortalButton variant="ghost" label={t('cancel')} onClick={closeModal} />
          <PortalButton label={cobro ? t('payCta', { amount }) : t('paySave')} disabled={cobro && !consent} onClick={() => setStep(2)} />
        </>
      ) : (
        <>
          <PortalModalSpacer />
          <PortalButton
            label={cobro ? t('payDoneCta') : t('done')}
            onClick={() => {
              closeModal();
              if (cobro) navigate(path('/status/solicitud', { state: 'active', dropView: true }));
            }}
          />
        </>
      )}
    >
      {step === 1 && (
        <>
          <p className="eyebrow">{cobro ? t('payEyebrow') : t('methodEyebrow')}</p>
          <h2>{cobro ? t('payTitle', { amount }) : t('methodTitle')}</h2>
          <p>{cobro ? t('payLead') : t('methodLead')}</p>
          <PortalToggle
            ariaLabel={t('methodTitle')}
            items={[
              { value: 'card', label: t('payCard') },
              { value: 'ach', label: t('payAch') },
            ]}
            value={method}
            onChange={(v) => setMethod(v as 'card' | 'ach')}
          />
          {method === 'card' ? (
            <div id="pay-card">
              <PortalInput id="pc-num" inputMode="numeric" label={t('payCardNumber')} placeholder="•••• •••• •••• 4242" style={{ marginBottom: 14 }} />
              <div className="row2">
                <PortalInput id="pc-exp" label={t('payExp')} placeholder="MM / AA" />
                <PortalInput id="pc-cvv" label={t('payCvv')} placeholder="•••" />
              </div>
            </div>
          ) : (
            <div id="pay-ach">
              <PortalInput id="pa-rou" inputMode="numeric" label={t('payRouting')} placeholder="123456789" style={{ marginBottom: 14 }} />
              <PortalInput id="pa-acc" inputMode="numeric" label={t('payAccount')} placeholder="•••••••1234" />
            </div>
          )}
          <label className="check" id="pay-consent" style={{ display: cobro ? 'flex' : 'none' }}>
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            {t('payConsent', { amount })}
          </label>
        </>
      )}
      {step === 2 && (
        <DoneBlock
          title={cobro ? t('payDoneTitle') : t('methodDoneTitle')}
          lead={cobro ? t('payDoneLead') : t('methodDoneLead')}
        />
      )}
    </PortalModal>
  );
}

function NotifyModal() {
  const t = usePortalCopy();
  const { step, setStep, closeModal } = usePortalUi();
  const phoneMark = '\u0000';
  const [notifyBefore, notifyAfter] = t('notifyLead', { phone: phoneMark }).split(phoneMark);
  return (
    <PortalModal
      id="m-notify"
      onClose={closeModal}
      closeLabel={t('close')}
      footer={step === 1 ? (
        <>
          <PortalModalSpacer />
          <PortalButton variant="ghost" label={t('notifyLater')} onClick={closeModal} />
          <PortalButton label={t('notifyActivate')} onClick={() => setStep(2)} />
        </>
      ) : (
        <>
          <PortalModalSpacer />
          <PortalButton label={t('done')} onClick={closeModal} />
        </>
      )}
    >
      {step === 1 && (
        <>
          <p className="eyebrow">{t('notifyEyebrow')}</p>
          <h2>{t('notifyTitle')}</h2>
          <p>
            {notifyBefore}
            <b className="masked">{PORTAL_FIXTURE.phoneMasked}</b>
            {notifyAfter}
          </p>
          <div className="uw-note">
            <b>{t('notifyNoteTitle')}</b>
            {t('notifyNote')}
          </div>
        </>
      )}
      {step === 2 && <DoneBlock title={t('notifyDoneTitle')} lead={t('notifyDoneLead')} />}
    </PortalModal>
  );
}
